// What a MIDI idea is, from its notes alone: its key (exact — the pitches
// are known), or, when it is a drum pattern, which drums.

import type { NewTag } from "@shared/api-types.js";
import type { WireNote } from "@shared/midi/notes.js";
import { kitVoice } from "@web-shared/lib/drums";
import { bestKey, type KeyEstimate } from "./key-profiles";

/** Pitch-class histogram weighted by how long and how hard each note sounds. */
export function pitchClassHistogram(notes: readonly WireNote[]): number[] {
  const h = new Array<number>(12).fill(0);
  for (const n of notes) {
    if (n.muted) continue;
    h[((n.pitch % 12) + 12) % 12]! += Math.max(0.05, n.duration) * (Math.max(1, n.velocity) / 127);
  }
  return h;
}

/** The key the notes sit in, or null for too few notes or an unclear fit. */
export function keyOfNotes(notes: readonly WireNote[]): KeyEstimate | null {
  const sounding = notes.filter((n) => !n.muted);
  if (new Set(sounding.map((n) => ((n.pitch % 12) + 12) % 12)).size < 3) return null;
  return bestKey(pitchClassHistogram(sounding));
}

/** The GM kit's core (kick to ride bell) sits in 35–59; a pattern mostly
 * there, in short notes, is drums rather than a melody. */
const GM_DRUM_LOW = 35;
const GM_DRUM_HIGH = 59;

const KIND_TO_TAG: Record<string, string> = {
  kick: "drums:kick",
  snare: "drums:snare",
  sidestick: "drums:snare",
  clap: "drums:clap",
  hatClosed: "drums:hihat",
  hatOpen: "drums:hihat",
  crash: "drums:cymbal",
  ride: "drums:cymbal",
  bell: "drums:cymbal",
  tom: "drums:tom",
  perc: "drums:percussion",
};

/** The drums a pattern plays, when ≥ 80% of its notes are GM drum pitches; else []. */
export function midiDrumTags(notes: readonly WireNote[]): string[] {
  const sounding = notes.filter((n) => !n.muted);
  if (sounding.length === 0) return [];
  const inKit = sounding.filter((n) => n.pitch >= GM_DRUM_LOW && n.pitch <= GM_DRUM_HIGH && n.duration <= 1);
  if (inKit.length / sounding.length < 0.8) return [];
  const kinds = new Set<string>();
  for (const n of inKit) kinds.add(String(kitVoice(n.pitch).kind));
  const tags = [...kinds].map((k) => KIND_TO_TAG[k]).filter((t): t is string => t !== undefined);
  const out = [...new Set(tags)];
  if (kinds.size >= 3) out.push("drums:drum-loop");
  return out;
}

/** What to suggest for a MIDI idea: its drums, else its key. */
export function suggestFromMidi(notes: readonly WireNote[]): NewTag[] {
  const drums = midiDrumTags(notes);
  if (drums.length > 0) return drums.map((tag) => ({ tag, source: "midi" }));
  const key = keyOfNotes(notes);
  return key ? [{ tag: key.tag, source: "midi" }] : [];
}
